import React, { useState } from "react";

// --- Narrative/Introduction Sections ---
const COVER_LETTER = `Dear Client,

Please find enclosed the Fire Risk Assessment for your premises, completed in accordance with the Regulatory Reform (Fire Safety) Order 2005 and PAS 79:2020 best practice. This report covers fire safety management, hazards, controls, and action plans.

Yours sincerely,
[Consultant Name & Signature]`;

const LEGAL_INTRO = `REGULATORY REFORM (FIRE SAFETY) ORDER 2005

This assessment was conducted in line with the above Order and PAS 79:2020. It identifies fire hazards, evaluates risks, and recommends practical improvements to protect people and property.

This report is for the client's exclusive use and may not be relied upon by others without written consent from the assessor.`;

// --- Objectives & Limitations ---
const OBJECTIVES = [
  "To identify and evaluate fire risks to people and property.",
  "To assess the effectiveness of existing fire precautions.",
  "To identify deficiencies and recommend appropriate control measures."
];

const LIMITATIONS = [
  "This assessment is non-destructive and based on visual inspection.",
  "Inaccessible areas at the time of assessment are excluded.",
  "No testing of electrical or fire systems was undertaken."
];

// --- General Information Table ---
function buildGeneralInfoTable(data) {
  return [
    ["Premises Assessed", data.siteAddress || ""],
    ["Assessment Title", data.auditTitle || ""],
    ["Assessment Reference", data.auditNumber || ""],
    ["Date of Assessment", data.dateConducted || ""],
    ["Assessor Name", data.auditCompletedBy || ""],
    ["Client/Responsible Person", data.auditPreparedFor || ""],
    ["Client Contact", data.clientContact || ""],
    ["Responsible Person (Onsite)", data.responsiblePerson || ""],
    ["Building Use", data.buildingUse || ""],
    ["Occupancy", data.occupancy || ""],
    ["Occupancy Hours", data.occupancyHours || ""],
    ["Number of Floors", data.numberOfFloors || ""],
    ["Floor Area per Floor (m²)", data.floorAreaPerFloor || ""],
    ["Construction/Layout", data.constructionLayout || ""]
  ];
}

// --- Executive Summary Table ---
function buildExecutiveSummaryTable(data) {
  return [
    ["Purpose", data.execPurpose || ""],
    ["Scope", data.execScope || ""],
    ["Strategy", data.execStrategy || ""],
    ["Fire History", data.execFireHistory || ""],
    ["Life Safety Concerns", data.execLifeSafetyConcerns || ""],
    ["Property Concerns", data.execPropertyConcerns || ""],
    ["Business Concerns", data.execBusinessConcerns || ""],
    ["Environmental Concerns", data.execEnvironmentalConcerns || ""],
    ["Conclusion", data.execConclusion || ""]
  ];
}

// --- Occupants Table ---
function buildOccupantsTable(data) {
  const occ = data.occupants || {};
  return [
    ["Sleeping", occ.sleeping?.has ? "Yes" : "No", occ.sleeping?.comments || ""],
    ["Disabled", occ.disabled?.has ? "Yes" : "No", occ.disabled?.comments || ""],
    ["Lone Workers", occ.loneWorkers?.has ? "Yes" : "No", occ.loneWorkers?.comments || ""],
    ["Young Persons", occ.youngPersons?.has ? "Yes" : "No", occ.youngPersons?.comments || ""],
    ["Visitors", occ.visitors?.has ? "Yes" : "No", occ.visitors?.comments || ""]
  ];
}

// --- Fire Loss Table ---
function buildFireLossTable(data) {
  return (data.fireLosses?.length ? data.fireLosses : [
    { date: "", nature: "", consequences: "" }
  ]).map(loss => [
    loss.date || "-", loss.nature || "-", loss.consequences || "-"
  ]);
}

// --- Legislation Table ---
function buildLegislationTable(data) {
  const arr = Array.isArray(data.legislation) && data.legislation.length
    ? data.legislation
    : [
        "Regulatory Reform (Fire Safety) Order 2005",
        "Building Regulations 2010: Approved Document B (Fire Safety)",
        "Management of Health and Safety at Work Regulations 1999",
        "BS 9999: Code of practice for fire safety in the design, management and use of buildings",
        "BS 5839: Fire detection and fire alarm systems for buildings",
        "BS 5306: Fire extinguishing installations and equipment on premises",
        "Health & Safety at Work etc. Act 1974"
      ];
  return arr.map(item => [item]);
}

// --- Risk Evaluation Definitions Table ---
const RISK_DEFINITIONS = [
  ["Risk", "Meaning"],
  ["Trivial", "No action is required, and no detailed records need be kept."],
  ["Tolerable", "No major additional controls required. However, there may be a need for improvements involving minor or limited cost."],
  ["Moderate", "Efforts should be made to reduce the risk. Risk reduction measures should be implemented within a defined time period."],
  ["Substantial", "Considerable resources may be required. If the building is unoccupied, it should not be used until risk is reduced."],
  ["Intolerable", "Building (or relevant area) should not be occupied until the risk is reduced."]
];

// --- Risk Matrix Table ---
const RISK_MATRIX = [
  ["Likelihood \\ Consequence", "Slight harm", "Moderate harm", "Extreme harm"],
  ["Highly Unlikely", "Trivial", "Tolerable", "Moderate"],
  ["Unlikely", "Tolerable", "Moderate", "Substantial"],
  ["Likely", "Moderate", "Substantial", "Intolerable"]
];

function riskColor(val) {
  if (!val) return "#fff";
  switch (val.toLowerCase()) {
    case "trivial": return "#7fff7f";
    case "tolerable": return "#fff47f";
    case "moderate": return "#ffc26f";
    case "substantial": return "#ff8383";
    case "intolerable": return "#d50000";
    default: return "#fff";
  }
}

// --- Fire Hazards Checklist: (PAS 79) ---
const FIRE_HAZARDS = [
  {
    main: "Fires of electrical origin",
    subs: [
      { key: "fixedInstallations", text: "Are all fixed electrical installations periodically inspected and tested?" },
      { key: "portableAppliances", text: "Are all portable appliances regularly tested/inspected?" },
      { key: "personalAppliances", text: "Are there adequate systems for control of personal portable appliances?" },
      { key: "extensionLeads", text: "Are extension leads and adaptors used safely and not overloaded?" }
    ]
  },
  {
    main: "Smoking-related fire risk",
    subs: [
      { key: "noSmokingPolicy", text: "Is there a strict no-smoking policy (except designated areas)?" },
      { key: "smokingArrangements", text: "Are arrangements in place for smokers?" },
      { key: "smokingSignage", text: "Is there evidence of enforcement and signage?" }
    ]
  },
  {
    main: "Arson prevention",
    subs: [
      { key: "arsonBinsSecure", text: "Are external bins, skips, and combustibles secure and away from buildings?" },
      { key: "arsonFireLoading", text: "Are perimeter/grounds checked for fire loading?" },
      { key: "arsonCCTVLighting", text: "Is lighting/CCTV provided as a deterrent?" }
    ]
  },
  {
    main: "Heating systems and portable heaters",
    subs: [
      { key: "heatingSystems", text: "Are heating systems/portable heaters regularly checked and safely positioned?" }
    ]
  },
  {
    main: "Cooking facilities",
    subs: [
      { key: "cookingArrangements", text: "Are all cooking facilities clean and fitted with fire blankets/extinguishers?" }
    ]
  },
  {
    main: "Lightning protection",
    subs: [
      { key: "lightningProtection", text: "Is there a current lightning protection certificate if required?" }
    ]
  },
  {
    main: "Escape routes and exits",
    subs: [
      { key: "housekeeping", text: "Are escape routes/exits kept clear and free of combustibles?" }
    ]
  },
  {
    main: "Contractor control",
    subs: [
      { key: "contractorsAwareness", text: "Are contractors supervised and aware of fire procedures?" }
    ]
  },
  {
    main: "Flammable substances",
    subs: [
      { key: "flammableSubstances", text: "Are flammable substances stored and used safely in line with regulations?" }
    ]
  },
  {
    main: "Fuel sources",
    subs: [
      { key: "fuelSources", text: "Are fuel sources (gas, oil) controlled and maintained?" }
    ]
  },
  {
    main: "Oxygen sources",
    subs: [
      { key: "oxygenSources", text: "Are oxygen sources (including medical gases) controlled and labelled?" }
    ]
  },
  {
    main: "Other significant fire hazards",
    subs: [
      { key: "otherHazards", text: "Are there any other significant fire hazards present?" }
    ]
  }
];

// --- Fire Protection Checklist ---
const FIRE_PROTECTION = [
  {
    main: "Fire-resistant doors and walls",
    subs: [
      { key: "fireDoorsGood", text: "Are fire doors fitted with self-closing devices and free from damage/gaps?" }
    ]
  },
  {
    main: "Emergency lighting",
    subs: [
      { key: "emergencyLighting", text: "Is emergency lighting provided, maintained, and functional?" }
    ]
  },
  {
    main: "Escape routes",
    subs: [
      { key: "escapeRoutes", text: "Are escape routes clearly marked, illuminated, and unobstructed?" }
    ]
  },
  {
    main: "Fire safety signage",
    subs: [
      { key: "signage", text: "Is fire safety signage clear, visible, and up to date throughout?" }
    ]
  },
  {
    main: "Fire detection and alarm system",
    subs: [
      { key: "detectionSystem", text: "Is a fire detection and alarm system installed and tested regularly?" }
    ]
  },
  {
    main: "Portable extinguishers",
    subs: [
      { key: "extinguishers", text: "Are the correct types and numbers of portable extinguishers provided and maintained?" }
    ]
  },
  {
    main: "Automatic extinguishing systems",
    subs: [
      { key: "autoExtinguishers", text: "Are any automatic extinguishing systems tested and serviced regularly?" }
    ]
  }
];

// --- Procedures & Management Checklist ---
const PROCEDURES = [
  {
    main: "Emergency plan",
    subs: [
      { key: "emergencyPlan", text: "Is there a documented and communicated emergency plan?" }
    ]
  },
  {
    main: "Summoning the fire brigade",
    subs: [
      { key: "callingBrigade", text: "Are procedures for summoning the fire brigade clearly outlined and understood?" }
    ]
  },
  {
    main: "Evacuation and accounting",
    subs: [
      { key: "evacuationChecks", text: "Are arrangements in place to account for all persons after evacuation?" }
    ]
  },
  {
    main: "High-risk persons",
    subs: [
      { key: "highRiskEvacuation", text: "Are there arrangements for the evacuation of high-risk persons (e.g. disabled, sleeping)?" }
    ]
  },
  {
    main: "Fire assembly point",
    subs: [
      { key: "fireAssembly", text: "Is there a designated and known fire assembly point?" }
    ]
  },
  {
    main: "Staff training",
    subs: [
      { key: "training", text: "Is all staff given fire safety training and induction?" }
    ]
  },
  {
    main: "Fire drills",
    subs: [
      { key: "drills", text: "Are regular fire drills conducted and properly recorded?" }
    ]
  },
  {
    main: "Testing and maintenance",
    subs: [
      { key: "testingMaintenance", text: "Is regular testing and maintenance carried out on fire safety systems?" }
    ]
  },
  {
    main: "Record keeping",
    subs: [
      { key: "recordKeeping", text: "Are records of training, tests, maintenance, and drills kept up to date?" }
    ]
  }
];

// --- Action Plan and Review Table Builders ---
function buildActionPlanTable(data) {
  return (data.actionPlan?.length ? data.actionPlan : [
    { issue: "", justification: "", currentMeasures: "", additionalControls: "", responsible: "", deadline: "" }
  ]).map(item => [
    item.issue || "-", item.justification || "-", item.currentMeasures || "-",
    item.additionalControls || "-", item.responsible || "-", item.deadline || "-"
  ]);
}

function buildReviewTable(data) {
  const r = data.review || {};
  return [
    ["Next Review Date", r.date || "-"],
    ["Reason for Review", r.reason || "-"],
    ["Follow-up Actions / Strategy", r.followUp || "-"]
  ];
}

// --- SIGNATURES Array for PDF/report use ---
const SIGNATURES = [
  ["Consultant Name & Signature", "Client Name & Signature", "Date"],
  ["", "", ""]
];

// --- Image Upload Helper ---
function ImageUpload({ images, setImages }) {
  const handleChange = e => {
    const files = Array.from(e.target.files);
    const previews = files.map(file => ({
      file,
      url: URL.createObjectURL(file)
    }));
    setImages(prev => [...prev, ...previews]);
  };
  const removeImage = idx => setImages(imgs => imgs.filter((_, i) => i !== idx));

  return (
    <div className="mb-2">
      <input
        type="file"
        accept="image/*"
        multiple
        className="block mb-2"
        onChange={handleChange}
      />
      <div className="flex flex-wrap gap-2">
        {images.map((img, idx) => (
          <div key={idx} className="relative w-28 h-28 rounded overflow-hidden border">
            <img
              src={img.url}
              alt="Uploaded"
              className="object-cover w-full h-full"
            />
            <button
              type="button"
              onClick={() => removeImage(idx)}
              className="absolute top-1 right-1 bg-black/50 text-white rounded-full px-2 py-0.5 text-xs"
              aria-label="Remove"
            >✕</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// --- Table Component ---
function Table({ rows, headers }) {
  return (
    <table className="w-full border rounded-xl shadow overflow-hidden text-base mb-6">
      <thead>
        {headers && (
          <tr className="bg-gray-200 text-gray-700">
            {headers.map((h, i) => (
              <th key={i} className="p-2 border-b">{h}</th>
            ))}
          </tr>
        )}
      </thead>
      <tbody>
        {rows.map((row, idx) => (
          <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
            {row.map((cell, i) => (
              <td key={i} className="p-2 border-b">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// --- Checklist Section ---
function ChecklistSection({ title, sections, answers, onChange, images, setImages }) {
  return (
    <section className="mb-8">
      <h3 className="text-lg font-semibold mb-4">{title}</h3>
      {sections.map((section, idx) => (
        <div key={idx} className="mb-4 border rounded-lg p-4 bg-gray-50 shadow-sm">
          <div className="font-medium text-gray-700 mb-1">{section.main}</div>
          {section.subs.map(sub => (
            <div key={sub.key} className="flex flex-col md:flex-row md:items-center gap-2 mb-2">
              <label className="flex-1">{sub.text}</label>
              <div className="flex gap-2 items-center">
                <input
                  type="radio"
                  id={`${sub.key}-yes`}
                  name={sub.key}
                  checked={answers[sub.key]?.answer === "Yes"}
                  onChange={() => onChange(sub.key, "Yes")}
                  className="accent-green-600"
                />
                <label htmlFor={`${sub.key}-yes`}>Yes</label>
                <input
                  type="radio"
                  id={`${sub.key}-no`}
                  name={sub.key}
                  checked={answers[sub.key]?.answer === "No"}
                  onChange={() => onChange(sub.key, "No")}
                  className="accent-red-600"
                />
                <label htmlFor={`${sub.key}-no`}>No</label>
                <input
                  type="text"
                  placeholder="Comments"
                  value={answers[sub.key]?.comments || ""}
                  onChange={e => onChange(sub.key, answers[sub.key]?.answer || "", e.target.value)}
                  className="ml-2 border rounded px-2 py-1 text-sm w-56"
                />
              </div>
            </div>
          ))}
          <ImageUpload images={images[section.main] || []} setImages={imgs => setImages(section.main, imgs)} />
        </div>
      ))}
    </section>
  );
}

// --- Main Fire Risk Assessment Form ---
export default function FireRiskAssessmentForm() {
  const [data, setData] = useState({
    siteAddress: "", auditTitle: "", auditNumber: "", dateConducted: "",
    auditCompletedBy: "", auditPreparedFor: "", clientContact: "",
    responsiblePerson: "", buildingUse: "", occupancy: "", occupancyHours: "",
    numberOfFloors: "", floorAreaPerFloor: "", constructionLayout: "",
    execPurpose: "", execScope: "", execStrategy: "", execFireHistory: "",
    execLifeSafetyConcerns: "", execPropertyConcerns: "", execBusinessConcerns: "",
    execEnvironmentalConcerns: "", execConclusion: "",
    occupants: { sleeping: {}, disabled: {}, loneWorkers: {}, youngPersons: {}, visitors: {} },
    fireLosses: [],
    legislation: [],
    actionPlan: [],
    review: {},
  });

  // --- Checklist Answers State (by section) ---
  const [fireHazardsAnswers, setFireHazardsAnswers] = useState({});
  const [fireProtectionAnswers, setFireProtectionAnswers] = useState({});
  const [proceduresAnswers, setProceduresAnswers] = useState({});
  // --- Images State (grouped by section main heading) ---
  const [sectionImages, setSectionImages] = useState({});

  // Helper for updating images by section heading
  const updateSectionImages = (section, imgs) => {
    setSectionImages(prev => ({ ...prev, [section]: imgs }));
  };

  // Checklist answers updater
  const handleChecklistAnswer = sectionSetter => (key, answer, comments = "") => {
    sectionSetter(prev => ({
      ...prev,
      [key]: { answer, comments }
    }));
  };

  // --- Render ---
  return (
    <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8 my-8">
      <h1 className="text-3xl font-bold text-center mb-6">Fire Risk Assessment</h1>
      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Cover Letter</h2>
        <div className="bg-gray-100 rounded p-4 whitespace-pre-line">{COVER_LETTER}</div>
      </section>
      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Legal Introduction</h2>
        <div className="bg-gray-100 rounded p-4 whitespace-pre-line">{LEGAL_INTRO}</div>
      </section>
      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Objectives & Limitations</h2>
        <ul className="list-disc pl-5 mb-2">{OBJECTIVES.map((o, i) => <li key={i}>{o}</li>)}</ul>
        <h3 className="text-lg font-semibold mt-4">Limitations</h3>
        <ul className="list-disc pl-5">{LIMITATIONS.map((l, i) => <li key={i}>{l}</li>)}</ul>
      </section>
      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">General Information</h2>
        <Table rows={buildGeneralInfoTable(data)} />
      </section>
      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Executive Summary</h2>
        <Table rows={buildExecutiveSummaryTable(data)} />
      </section>
      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Occupants</h2>
        <Table rows={buildOccupantsTable(data)} headers={["Type", "Present", "Comments"]} />
      </section>
      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Fire Losses</h2>
        <Table rows={buildFireLossTable(data)} headers={["Date", "Nature", "Consequences"]} />
      </section>
      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Legislation</h2>
        <Table rows={buildLegislationTable(data)} headers={["Applicable Legislation"]} />
      </section>
      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Risk Definitions</h2>
        <Table rows={RISK_DEFINITIONS} />
      </section>
      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Risk Matrix</h2>
        <table className="w-full border rounded-xl shadow overflow-hidden text-base mb-6">
          <tbody>
            {RISK_MATRIX.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td
                    key={j}
                    className="p-2 border-b text-center font-medium"
                    style={{
                      background: i === 0 || j === 0 ? "#f3f4f6" : riskColor(cell)
                    }}
                  >{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <ChecklistSection
        title="Fire Hazards Checklist"
        sections={FIRE_HAZARDS}
        answers={fireHazardsAnswers}
        onChange={handleChecklistAnswer(setFireHazardsAnswers)}
        images={sectionImages}
        setImages={updateSectionImages}
      />
      <ChecklistSection
        title="Fire Protection Checklist"
        sections={FIRE_PROTECTION}
        answers={fireProtectionAnswers}
        onChange={handleChecklistAnswer(setFireProtectionAnswers)}
        images={sectionImages}
        setImages={updateSectionImages}
      />
      <ChecklistSection
        title="Procedures & Management"
        sections={PROCEDURES}
        answers={proceduresAnswers}
        onChange={handleChecklistAnswer(setProceduresAnswers)}
        images={sectionImages}
        setImages={updateSectionImages}
      />
      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Action Plan</h2>
        <Table
          headers={["Issue", "Justification", "Current Measures", "Additional Controls", "Responsible", "Deadline"]}
          rows={buildActionPlanTable(data)}
        />
      </section>
      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Review</h2>
        <Table
          headers={["Item", "Details"]}
          rows={buildReviewTable(data)}
        />
      </section>
      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">Signatures</h2>
        <Table
          headers={["Consultant Name & Signature", "Client Name & Signature", "Date"]}
          rows={SIGNATURES.slice(1)}
        />
      </section>
    </div>
  );
}

// --- Export helpers for PDF/Docx/other utilities ---
export {
  COVER_LETTER,
  LEGAL_INTRO,
  OBJECTIVES,
  LIMITATIONS,
  buildGeneralInfoTable,
  buildExecutiveSummaryTable,
  buildOccupantsTable,
  buildFireLossTable,
  buildLegislationTable,
  RISK_DEFINITIONS,
  RISK_MATRIX,
  riskColor,
  FIRE_HAZARDS,
  FIRE_PROTECTION,
  PROCEDURES,
  buildActionPlanTable,
  buildReviewTable,
  SIGNATURES
};
